import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError } from '@/api/client';
import { consumablesApi, organizationApi } from '@/api/endpoints';
import { queryKeys } from '@/api/queryKeys';
import type { Consumable } from '@/api/types';
import { Plus } from 'lucide-react';
import { usePermission } from '@/auth/AuthProvider';
import { PERMISSIONS } from '@/auth/permissions';
import { Button } from '@/design-system/Button';
import { InlineError } from '@/design-system/Feedback';
import { Select, TextArea, TextInput } from '@/design-system/Inputs';
import { Dialog } from '@/design-system/Overlay';
import { QuickLocationDialog } from '../assets/QuickCatalogDialogs';

interface FormValues {
  sku: string;
  name: string;
  category: string;
  unit: string;
  description: string;
  reorderLevel: string;
  openingQuantity: string;
  unitCost: string;
  currency: string;
  supplier: string;
  locationId: string;
}

/**
 * Units offered as a list rather than a free-text box.
 *
 * Free text gives you "each", "ea", "Each" and "pcs" for the same thing within a week, and no
 * report can then group by unit. The list is short because these are the units stock is actually
 * counted in.
 */
const UNITS = ['each', 'box', 'pack', 'metre', 'litre', 'kilogram', 'roll', 'set'];

/** Adding a stocked item to the catalogue. */
export function CreateConsumableDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (consumable: Consumable) => void;
}) {
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<unknown>(null);

  const canEditOrg = usePermission(PERMISSIONS.ORG_WRITE);
  const [addingLocation, setAddingLocation] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      sku: '',
      name: '',
      category: '',
      unit: 'each',
      description: '',
      reorderLevel: '0',
      openingQuantity: '0',
      unitCost: '',
      currency: '',
      supplier: '',
      locationId: '',
    },
  });

  const locations = useQuery({
    queryKey: queryKeys.organization.locations(),
    queryFn: organizationApi.locations,
    enabled: open,
    staleTime: 10 * 60_000,
  });

  // Offered as suggestions rather than a fixed list: categories are the client's own vocabulary,
  // but showing what already exists is what stops "Cables" and "cables" both being created.
  const categories = useQuery({
    queryKey: queryKeys.consumables.categories(),
    queryFn: consumablesApi.categories,
    enabled: open,
    staleTime: 5 * 60_000,
  });

  const create = useMutation({
    mutationFn: (values: FormValues) =>
      consumablesApi.create({
        sku: values.sku.trim(),
        name: values.name.trim(),
        category: values.category.trim(),
        unit: values.unit,
        description: values.description || undefined,
        reorderLevel: Number(values.reorderLevel) || 0,
        openingQuantity: Number(values.openingQuantity) || 0,
        unitCost: values.unitCost || undefined,
        currency: values.currency ? values.currency.toUpperCase() : undefined,
        supplier: values.supplier || undefined,
        locationId: values.locationId || undefined,
      }),
    onSuccess: (consumable) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.consumables.all });
      toast.success('Consumable added', { description: consumable.name ?? undefined });
      reset();
      onCreated(consumable);
    },
    onError: (error) => {
      if (error instanceof ApiError && error.fieldErrors.length > 0) {
        error.fieldErrors.forEach((fieldError) => {
          setError(fieldError.field as keyof FormValues, { message: fieldError.message });
        });
        setSubmitError(null);
      } else {
        setSubmitError(error);
      }
    },
  });

  const close = () => {
    if (isSubmitting || create.isPending) return;
    setSubmitError(null);
    reset();
    onClose();
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={close}
      title="Add a consumable"
      description="Stock counted by quantity - cables, keyboards, toner. Anything tracked individually with a lifecycle belongs in the asset register instead."
      size="lg"
      busy={create.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={create.isPending}
            onClick={handleSubmit((values) => create.mutate(values))}
          >
            Add consumable
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={handleSubmit((values) => create.mutate(values))}
        noValidate
      >
        {submitError ? <InlineError error={submitError} /> : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <TextInput
            label="SKU"
            required
            placeholder="CBL-USBC-2M"
            description="Your own code for this item. It cannot be changed later."
            error={errors.sku?.message}
            {...register('sku', {
              required: 'A SKU is required',
              pattern: {
                value: /^[A-Za-z0-9._/-]+$/,
                message: 'Letters, digits and . _ / - only',
              },
            })}
          />
          <TextInput
            label="Name"
            required
            placeholder="USB-C cable, 2 m"
            error={errors.name?.message}
            {...register('name', { required: 'A name is required' })}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <TextInput
            label="Category"
            required
            list="consumable-categories"
            placeholder="Cables"
            error={errors.category?.message}
            {...register('category', { required: 'A category is required' })}
          />
          <datalist id="consumable-categories">
            {(categories.data ?? []).map((category) => (
              <option key={category} value={category} />
            ))}
          </datalist>
          <Select
            label="Unit"
            required
            options={UNITS.map((unit) => ({ value: unit, label: unit }))}
            error={errors.unit?.message}
            {...register('unit', { required: 'Choose a unit' })}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <TextInput
            label="Opening quantity"
            type="number"
            min={0}
            description="What is on the shelf now. Recorded as a receipt, so the ledger explains the starting figure."
            error={errors.openingQuantity?.message}
            {...register('openingQuantity', {
              min: { value: 0, message: 'Cannot be negative' },
            })}
          />
          <TextInput
            label="Reorder level"
            type="number"
            min={0}
            description="Flagged as low stock at or below this. Zero switches the warning off."
            error={errors.reorderLevel?.message}
            {...register('reorderLevel', { min: { value: 0, message: 'Cannot be negative' } })}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <TextInput
            label="Unit cost"
            type="number"
            step="0.01"
            min={0}
            error={errors.unitCost?.message}
            {...register('unitCost', { min: { value: 0, message: 'Cannot be negative' } })}
          />
          <TextInput
            label="Currency"
            placeholder="GBP"
            maxLength={3}
            error={errors.currency?.message}
            {...register('currency', {
              pattern: { value: /^[A-Za-z]{3}$|^$/, message: 'Three-letter code' },
            })}
          />
          <div className="flex items-end gap-2">
            <div className="min-w-0 flex-1">
              <Select
                label="Location"
                placeholder="No location"
                options={[
                  { value: '', label: 'No location' },
                  ...(locations.data ?? []).map((location) => ({
                    value: location.id ?? '',
                    label: location.name ?? '',
                  })),
                ]}
                {...register('locationId')}
              />
            </div>
            {canEditOrg ? (
              <Button
                variant="secondary"
                aria-label="Add a location"
                title="Add a location"
                onClick={() => setAddingLocation(true)}
                className="mb-px h-9 shrink-0 px-3"
              >
                <Plus className="size-4" />
              </Button>
            ) : null}
          </div>
        </div>

        <TextInput label="Supplier" placeholder="Who it is bought from" {...register('supplier')} />

        <TextArea label="Description" rows={2} {...register('description')} />
      </form>
    </Dialog>

    <QuickLocationDialog
      open={addingLocation}
      onClose={() => setAddingLocation(false)}
      onCreated={(location) => {
        setAddingLocation(false);
        setValue('locationId', location.id ?? '', { shouldValidate: true });
      }}
    />
    </>
  );
}
